/**
 * Generate realistic OHLCV sample data for indicator preview
 * Uses a random walk with mean reversion for realistic price action
 *
 * @param {number} count - Number of candles to generate
 * @param {number} startPrice - Starting price
 * @returns {{ open: Float64Array, high: Float64Array, low: Float64Array, close: Float64Array, volume: Float64Array }}
 */
export function generateSampleData(count = 500, startPrice = 100) {
    const open = new Float64Array(count);
    const high = new Float64Array(count);
    const low = new Float64Array(count);
    const close = new Float64Array(count);
    const volume = new Float64Array(count);

    // Seed for reproducibility
    let seed = 42;
    function random() {
        seed = (seed * 16807 + 0) % 2147483647;
        return (seed - 1) / 2147483646;
    }

    // Box-Muller for normal distribution
    function randn() {
        const u1 = random();
        const u2 = random();
        return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
    }

    let price = startPrice;
    const meanPrice = startPrice;
    const volatility = 0.015; // 1.5% daily volatility
    const meanReversion = 0.001;

    for (let i = 0; i < count; i++) {
        // Mean-reverting random walk
        const drift = -meanReversion * (price - meanPrice);
        const shock = volatility * price * randn();
        const newPrice = price + drift + shock;

        const o = price;
        const c = Math.max(0.01, newPrice);

        // High/Low with realistic wicks
        const range = Math.abs(c - o);
        const wickUp = Math.abs(randn()) * range * 0.5;
        const wickDown = Math.abs(randn()) * range * 0.5;

        const h = Math.max(o, c) + wickUp;
        const l = Math.min(o, c) - wickDown;

        open[i] = o;
        high[i] = h;
        low[i] = Math.max(0.01, l);
        close[i] = c;
        volume[i] = Math.max(100, 1000000 + randn() * 300000);

        price = c;
    }

    return { open, high, low, close, volume };
}

/**
 * Generate large dataset for performance testing
 * @param {number} count
 * @returns {Object}
 */
export function generateLargeDataset(count = 100000) {
    return generateSampleData(count, 50000);
}

export default generateSampleData;
